/**
 * @Copyright (c) Microsoft Corporation.  All rights reserved.
 */

interface IKeyValuePair {
  key: string;
  value: string;
}

/**
 * Class for storing and retrieving query parameters.
 * The URL can be server-relative and it will parse empty/null strings.
 * The query parameters must start with ? to indicate the first query parameter and
 * use & for all subsequent parameters. The class also supports fragments.
 * Edge cases behavior:
 * Empty value (www.example.com/?test=) stores key and empty value
 * No equals in queryParam (www.example.com/?test) stores key and undefined value
 * Empty queryParam (www.example.com/?&debug=on) stores undefined key and value
 * Query param with only equals (www.example.com/?=&debug=on stores empty string key and value
 * @public
 * @internalremarks UrlQueryParameterCollection is being re-exported by sp-client-base and should
 * be imported from there.
 */
export default class UrlQueryParameterCollection {
  private _queryParameterList: IKeyValuePair[];
  constructor(url: string) {
    this._queryParameterList = this._getUrlQueryParameterArray(url);
  }

  /**
   * Returns the value of the first matching query parameter or undefined if the key doesn't exist.
   * Examples: this._queryParameterList = [
   * {key: TEST, value: done},
   * {key: DEBUG, value: false},
   * {key: TEST, value: notdone}]
   *   getValue('TEST') ---> 'done'
   *   getValue('debug')  ---> 'false'
   *   getValue('lost')  ---> undefined
   * @param param the case insensitive key for the desired query parameter value.
   */
  public getValue(param: string): string {
    for (const queryParam of this._queryParameterList) {
      if (param === undefined) {
        if (queryParam.key === undefined) {
          return queryParam.value;
        }
        continue;
      } else if (param.toUpperCase() === queryParam.key) {
        return queryParam.value;
      }
    }
    return undefined;
  }

  /**
   * Returns the values of all of the matching query parameters or undefined if the key doesn't exist.
   * Examples: this._queryParameterList = [
   * {key: TEST, value: done},
   * {key: DEBUG, value: false},
   * {key: TEST, value: notdone}]
   *   getValues('TEST') ---> ['done', 'notdone']
   *   getValues('debug')  ---> ['false']
   *   getValues('lost')  ---> undefined
   * @param param the case insensitive key for the desired query parameter value.
   */
  public getValues(param: string): string[] {
    const queryParams: string[] = [];
    for (const queryParam of this._queryParameterList) {
      if (param === undefined) {
        if (queryParam.key === undefined) {
          queryParams.push(queryParam.value);
        }
        continue;
      } else if (param.toUpperCase() === queryParam.key) {
        queryParams.push(queryParam.value);
      }
    }
    return queryParams.length > 0 ? queryParams : undefined;
  }

  private _getUrlQueryParameterArray(url: string): IKeyValuePair[] {
    const queryParameterList: IKeyValuePair[] = [];
    const urlParser: HTMLAnchorElement = document.createElement('a');
    urlParser.href = url;
    const queryParams: string[] = urlParser.search.substring(1).split('&');
    for (const queryParam of queryParams) {
      if (queryParam !== '') {
        const equalsIndex: number = queryParam.indexOf('=');
        let key: string;
        let value: string;

        if (equalsIndex < 0) {
          key = queryParam;
          value = undefined;
        } else if (equalsIndex === 0) {
          key = '';
          value = queryParam.length === 1 ? '' : queryParam.substring(1);
        } else {
          key = queryParam.substring(0, equalsIndex);
          value =
            queryParam.substring(equalsIndex).length === 1 ?
              '' :
              queryParam.substring(equalsIndex + 1);
        }
        queryParameterList.push({
          key: decodeURIComponent(key.toUpperCase()),
          value: value
        });
      } else {
        queryParameterList.push({
          key: undefined,
          value: undefined
        });
      }
    }
    return queryParameterList;
  }
}


// WEBPACK FOOTER //
// /src/staticLinks/UrlQueryParameterCollection.ts


// WEBPACK FOOTER //
// webpack:///src/staticLinks/UrlQueryParameterCollection.ts